@if (!api() || !assets()) {
  <app-please-wait></app-please-wait>
}
@if (api() && assets()) {
  <article>
    <div class="no-screen">
      <div class="print-header">
        <!--<img src="https://4d-org-logos.s3.eu-west-2.amazonaws.com/chp-logo.png" />-->
        <h1>4D API Documentation</h1>
      </div>
    </div>

    <section class="no-print text-right">
      <app-button (click)="printPage()" label="Print Documentation" icon="mdi mdi-printer" />
    </section>

    <section>
      <h2>Overview</h2>
      <p>
        This documentation will enable access to data for
        <b>{{ this.apiService.getUserOrg().title }} </b>.
      </p>
      <p>Return current or historic sensor (asset) values for individual assets, assets on a gateway or the entire catalog of assets.</p>
    </section>

    <section>
      <h2>Using the REST API</h2>
      <p>To request telemetry from 4D you'll need to supply the 4D API key in the header of your API call.</p>

      <table class="table table-sm">
        <thead>
          <tr>
            <th>Header Key</th>
            <th>Header Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>x-api-key</td>
            <td>{{ api().apikey }}</td>
          </tr>
        </tbody>
      </table>
    </section>
    <section>
      <h2>Example asset response</h2>
      <p>Data is returned as json similar to the following</p>
      <pre>
        &lcub;
        "id":12345,
        "createdAt":"2022-03-17T12:30:33.000Z",
        "uom":"°C",
        "category":"temperature",
        "value":"14.5"
        &rcub;</pre
      >
    </section>
    <section>
      <h2>Request sites</h2>
      <app-dev-curl [api]="api()" path="sites"></app-dev-curl>
      <table>
        <thead>
          <tr>
            <th>Property</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>id</td>
            <td>Site Id</td>
          </tr>
          <tr>
            <td>title</td>
            <td>Site title</td>
          </tr>
          <tr>
            <td>address1</td>
            <td>First address line of site</td>
          </tr>
          <tr>
            <td>address2</td>
            <td>Second address line (if any)</td>
          </tr>
          <tr>
            <td>addressTown</td>
            <td>Site Town</td>
          </tr>
          <tr>
            <td>addressCounty</td>
            <td>Site County</td>
          </tr>
          <tr>
            <td>addressPostcode</td>
            <td>Site Postcode</td>
          </tr>
          <tr>
            <td>addressLong</td>
            <td>Site Longitude</td>
          </tr>
          <tr>
            <td>addressLat</td>
            <td>Site Latitude</td>
          </tr>
          <tr>
            <td>org</td>
            <td>The organisation the site belongs to</td>
          </tr>
          <tr>
            <td>metobsid</td>
            <td>The weather id (see endpoint)</td>
          </tr>
        </tbody>
      </table>
    </section>
    <section>
      <h2>Request gateways</h2>
      <app-dev-curl [api]="api()" path="gateways"></app-dev-curl>
      <table>
        <thead>
          <tr>
            <th>Property</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>id</td>
            <td>Gateway id</td>
          </tr>
          <tr>
            <td>title</td>
            <td>Gateway title</td>
          </tr>
          <tr>
            <td>location</td>
            <td>Location of the gateway (if any)</td>
          </tr>
          <tr>
            <td>siteId</td>
            <td>Site id of the gateway</td>
          </tr>
          <tr>
            <td>site</td>
            <td>Site title of the gateway</td>
          </tr>
        </tbody>
      </table>
    </section>
    @if (assetSelected()) {
      <section>
        <h2>Request latest telemetry for all assets</h2>
        <app-dev-curl [api]="api()" path="assets"></app-dev-curl>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>id</td>
              <td>Asset id</td>
            </tr>
            <tr>
              <td>title</td>
              <td>Asset title</td>
            </tr>
            <tr>
              <td>value</td>
              <td>Latest telemetry value for the asset</td>
            </tr>
            <tr>
              <td>updatedAt</td>
              <td>Latest telemetry timestamp</td>
            </tr>
            <tr>
              <td>inError</td>
              <td>Is the asset in error (Y/N)</td>
            </tr>
            <tr>
              <td>assetType</td>
              <td>Asset type</td>
            </tr>
            <tr>
              <td>dataType</td>
              <td>Data type</td>
            </tr>
            <tr>
              <td>uom</td>
              <td>Unit of measure</td>
            </tr>
            <tr>
              <td>category</td>
              <td>Asset type category</td>
            </tr>
            <tr>
              <td>gateway_id</td>
              <td>Gateway id of the asset</td>
            </tr>
          </tbody>
        </table>
      </section>
      <section>
        <h2>Request latest telemetry for all assets with site and identifier</h2>
        <app-dev-curl [api]="api()" path="assets?identifiers=1"></app-dev-curl>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>site</td>
              <td>Asset site</td>
            </tr>
            <tr>
              <td>id</td>
              <td>Asset id</td>
            </tr>
            <tr>
              <td>identifier</td>
              <td>Asset identifier</td>
            </tr>
            <tr>
              <td>title</td>
              <td>Asset title</td>
            </tr>
            <tr>
              <td>type</td>
              <td>Asset type</td>
            </tr>
            <tr>
              <td>value</td>
              <td>Latest telemetry value for the asset</td>
            </tr>
          </tbody>
        </table>
      </section>
    }
    @if (gatewaySelected()) {
      <section>
        <h2>Request latest telemetry for all gateway assets</h2>
        <p>In this example we get the latest asset telemetry for asset id {{ assetSelected()?.id }}</p>
        <app-dev-curl [api]="api()" path="assets?gateway_id={{ gatewaySelected()?.id }}"></app-dev-curl>
      </section>
    }
    @if (assetSelected()) {
      <section>
        <h2>Request latest telemetry for an asset</h2>
        <p>In this example we get the latest asset telemetry for asset id {{ assetSelected()?.id }}</p>
        <app-dev-curl [api]="api()" path="assets?id={{ assetSelected()?.id }}"></app-dev-curl>
      </section>
    }
    @if (assetSelected()) {
      <section class="page-break">
        <h2>Request telemetry for asset(s) in date range</h2>
        <p>In this example we get the the asset telemetry from yesterday to today, for asset id {{ assetSelected()?.id }}</p>
        <app-dev-curl [api]="api()" path="assets?id={{ assetSelected()?.id }}&date_from={{ yesterdayCurlDate }}&date_to={{ todayCurlDate }}"> </app-dev-curl>
        <p>You can include multiple assets, for example id={{ assetSelected()?.id }}&id={{ assets()[1]?.id }}</p>
        <app-dev-curl [api]="api()" path="assets?id={{ assetSelected()?.id }}&id={{ assets()[1]?.id }}&date_from={{ yesterdayCurlDate }}&date_to={{ todayCurlDate }}"> </app-dev-curl>
        <p>Return all assets by omitting id(s)</p>
        <app-dev-curl [api]="api()" path="assets?date_from={{ yesterdayCurlDate }}&date_to={{ todayCurlDate }}"> </app-dev-curl>
      </section>
    }
    @if (assetSelected()) {
      <section class="page-break">
        <h2>Request weather data</h2>
        <p>In this example we get all weather data for yesterday</p>
        <app-dev-curl [api]="api()" path="weather?date_from={{ yesterdayCurlDate }}&date_to={{ yesterdayCurlDate }}"> </app-dev-curl>
        <p>You can include multiple metobsids (found in /v2/sites), for example id={{ siteSelected()?.metobsid }}</p>
        <app-dev-curl [api]="api()" path="weather?id={{ siteSelected()?.metobsid }}&date_from={{ yesterdayCurlDate }}&date_to={{ yesterdayCurlDate }}"> </app-dev-curl>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>metobsid</td>
              <td>Weather id</td>
            </tr>
            <tr>
              <td>date</td>
              <td>Date of the weather</td>
            </tr>
            <tr>
              <td>hour</td>
              <td>Hour of the weather</td>
            </tr>
            <tr>
              <td>temp</td>
              <td>Recorded temperature</td>
            </tr>
            <tr>
              <td>pressure</td>
              <td>Recorded pressure</td>
            </tr>
            <tr>
              <td>wind_speed</td>
              <td>Recorded wind speed</td>
            </tr>
            <tr>
              <td>wind_deg</td>
              <td>Recorded wind direction</td>
            </tr>
            <tr>
              <td>humidity</td>
              <td>Recorded humidity</td>
            </tr>
            <tr>
              <td>timestamp</td>
              <td>Recorded timestamp</td>
            </tr>
          </tbody>
        </table>
      </section>
    }
    <section class="pagebreakbefore">
      <h2>Unit of measures (uom)</h2>
      <table class="table">
        <thead>
          <tr>
            <td></td>
          </tr>
        </thead>
        <tbody>
          @for (uom of uoms; track uom) {
            <tr>
              <td>{{ uom.id }}</td>
              <td>{{ uom.title }}</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  </article>
}
